import { Text } from '@user-portal/wikr-components';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createProcessingSelector } from 'store/loading/selectors';
import { updateUserPersonalisationRequest } from 'store/user/actions';
import { GET_USER_PERSONALISATION, UPDATE_USER_PERSONALISATION } from 'store/user/actionTypes';
import { selectCurrentUser } from 'store/user/selectors';

import { FitnessLevel } from 'constants/user';

import { useDispatch, useSelector } from 'hooks';
import useModals from 'modals/hooks';

import { CustomPrimaryButton, OverlineButton } from 'components/Buttons';
import Modal from 'components/Modal/Modal';

import FitnessLevelCard from './components/FitnessLevelCard';
import { FITNESS_LEVEL_CARDS } from './components/FitnessLevelCard/helpers';

import * as S from './styled';

const FitnessLevelModal = () => {
    const dispatch = useDispatch();

    const { fitness_level } = useSelector(selectCurrentUser);
    const { t } = useTranslation();
    const { closeModal } = useModals();

    const [selectedFitnessLevel, setSelectedFitnessLevel] = useState<FitnessLevel>(
        fitness_level ?? FitnessLevel.Beginner
    );

    const isLoading: boolean = useSelector(
        createProcessingSelector([UPDATE_USER_PERSONALISATION, GET_USER_PERSONALISATION])
    );

    const isSaveButtonDisabled = selectedFitnessLevel === fitness_level;

    const onCardClick = useCallback((level: FitnessLevel) => {
        setSelectedFitnessLevel(level);
    }, []);

    const onSave = () => {
        dispatch(
            updateUserPersonalisationRequest({
                payload: {
                    level: selectedFitnessLevel,
                },
                onSuccess: () => {
                    closeModal();
                },
            })
        );
    };

    return (
        <Modal
            onClose={closeModal}
            withRoundedCorners={false}
            dataLocator="yogaLevelModal"
            mobileFullScreen
            isOpen
            shouldCloseOnOverlayClick
        >
            <S.ContentWrapper data-locator="fitnessLevelModal">
                <Text type="h4" center>
                    {t('account.planSettings.yogaLevel')}
                </Text>
                <S.ModalContent>
                    {FITNESS_LEVEL_CARDS.map(({ title, description, id, ...rest }) => (
                        <FitnessLevelCard
                            isActive={id === selectedFitnessLevel}
                            title={t(title)}
                            description={t(description)}
                            key={id}
                            onClick={onCardClick}
                            id={id}
                            {...rest}
                        />
                    ))}
                </S.ModalContent>
                <div>
                    <CustomPrimaryButton
                        dataLocator="save"
                        disabled={isSaveButtonDisabled}
                        mb={20}
                        onClick={onSave}
                        textColor="text-main"
                        isLoading={isLoading}
                    >
                        {t('basics.save')}
                    </CustomPrimaryButton>
                    <OverlineButton dataLocator="cancelBtn" onClick={closeModal} text={t('basics.cancel')} />
                </div>
            </S.ContentWrapper>
        </Modal>
    );
};

export default FitnessLevelModal;
